const TARGET_SAMPLE_RATE = 16_000
const MAX_CHUNK_BYTES = 18 * 1024 * 1024
const BROWSER_RECORDING_MIME = /^audio\/(webm|ogg|mp4)(?:;|$)/i

const writeString = (view: DataView, offset: number, value: string) => {
  for (let index = 0; index < value.length; index += 1) view.setUint8(offset + index, value.charCodeAt(index))
}

const encodeWav = (samples: Float32Array, sampleRate: number) => {
  const buffer = new ArrayBuffer(44 + samples.length * 2)
  const view = new DataView(buffer)
  writeString(view, 0, 'RIFF')
  view.setUint32(4, 36 + samples.length * 2, true)
  writeString(view, 8, 'WAVE')
  writeString(view, 12, 'fmt ')
  view.setUint32(16, 16, true)
  view.setUint16(20, 1, true)
  view.setUint16(22, 1, true)
  view.setUint32(24, sampleRate, true)
  view.setUint32(28, sampleRate * 2, true)
  view.setUint16(32, 2, true)
  view.setUint16(34, 16, true)
  writeString(view, 36, 'data')
  view.setUint32(40, samples.length * 2, true)
  let offset = 44
  samples.forEach((sample) => {
    const normalized = Math.max(-1, Math.min(1, sample))
    view.setInt16(offset, normalized < 0 ? normalized * 0x8000 : normalized * 0x7fff, true)
    offset += 2
  })
  return new Blob([buffer], { type: 'audio/wav' })
}

const mergeToMono = (buffer: AudioBuffer) => {
  const output = new Float32Array(buffer.length)
  for (let channel = 0; channel < buffer.numberOfChannels; channel += 1) {
    const data = buffer.getChannelData(channel)
    for (let index = 0; index < data.length; index += 1) output[index] += data[index] / buffer.numberOfChannels
  }
  return output
}

const resample = (samples: Float32Array, sourceRate: number) => {
  if (sourceRate === TARGET_SAMPLE_RATE) return samples
  const ratio = sourceRate / TARGET_SAMPLE_RATE
  const output = new Float32Array(Math.ceil(samples.length / ratio))
  for (let index = 0; index < output.length; index += 1) {
    const position = index * ratio
    const left = Math.floor(position)
    const right = Math.min(left + 1, samples.length - 1)
    const weight = position - left
    output[index] = samples[left] * (1 - weight) + samples[right] * weight
  }
  return output
}

export const shouldNormalizeAudioForTranscription = (file: Pick<Blob, 'size' | 'type'>) =>
  file.size > MAX_CHUNK_BYTES || BROWSER_RECORDING_MIME.test(file.type)

export const splitAudioForTranscription = async (file: Blob) => {
  // MediaRecorder produces fragmented WebM/MP4/OGG containers. Browsers can play
  // those blobs, but external decoders may reject them as corrupted. Decode the
  // complete recording in the browser and send a plain 16 kHz mono WAV instead.
  if (!shouldNormalizeAudioForTranscription(file)) return [file]
  const context = new AudioContext()
  try {
    const decoded = await context.decodeAudioData(await file.arrayBuffer())
    const mono = resample(mergeToMono(decoded), decoded.sampleRate)
    if (!mono.length) throw new Error('Decoded audio is empty')
    const samplesPerChunk = Math.floor((MAX_CHUNK_BYTES - 44) / 2)
    const chunks: Blob[] = []
    for (let start = 0; start < mono.length; start += samplesPerChunk) {
      chunks.push(encodeWav(mono.slice(start, start + samplesPerChunk), TARGET_SAMPLE_RATE))
    }
    return chunks
  } catch {
    throw new Error('Бұл үлкен аудио форматты браузер бөле алмады. Файлды MP3, M4A, WAV немесе WebM форматына ауыстырып көріңіз.')
  } finally {
    await context.close()
  }
}

export const mergeTranscripts = (parts: string[]) => {
  let result = ''
  parts.forEach((part) => {
    const clean = part.trim()
    if (!result) {
      result = clean
      return
    }
    const previousWords = result.split(/\s+/)
    const nextWords = clean.split(/\s+/)
    let overlap = 0
    const maxOverlap = Math.min(30, previousWords.length, nextWords.length)
    for (let count = maxOverlap; count >= 3; count -= 1) {
      const previous = previousWords.slice(-count).join(' ').toLocaleLowerCase()
      const next = nextWords.slice(0, count).join(' ').toLocaleLowerCase()
      if (previous === next) {
        overlap = count
        break
      }
    }
    result = `${result.trim()} ${nextWords.slice(overlap).join(' ')}`.trim()
  })
  return result
}

export const getSupportedRecordingMimeType = () => {
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/mp4;codecs=mp4a.40.2',
  ]
  return candidates.find((type) => window.MediaRecorder?.isTypeSupported(type)) || ''
}
